import { ViewportPortal } from '@xyflow/react'

import {
  BoundaryEventType,
  BoundaryEventTypeByNodeType,
} from '@flowx/designer/shared/entities/boundary-event/boundary-event.common'
import { NodeType } from '@flowx/designer/shared/entities/node.types'

import { GraphColors } from '../../../bpmn-graph.constants'
import { Point } from '../../../dictionary/geometry.types'
import { BpmnNodeKind, getNodeVisuals, recolorIconSvg, recolorShapeSvg } from '../node-visuals'

export interface PaletteDropPreviewState {
  /** Preview center in flow coordinates — the cursor, or the border projection when snapped */
  position: Point
  /** Drop validity — green border when true, red when false */
  valid: boolean
  /** Over a valid boundary-event target: render as the boundary event snapped on the node border */
  snappedBoundary: boolean
}

/**
 * In-canvas preview of the node being dragged from the palette: the final node's
 * own shape and type icon, white fill at 0.7 opacity, centered under the cursor
 * and tinted green/red by drop validity. Over a valid boundary-event target it
 * instead renders the boundary event the drop will create, untinted and snapped
 * on the target node's border. Rendered through ViewportPortal
 * so it pans/zooms with the diagram like the real node will.
 */
export default function PaletteDropPreview({
  type,
  preview,
}: {
  type: NodeType | BoundaryEventType
  preview: PaletteDropPreviewState
}) {
  const effectiveType = preview.snappedBoundary ? (BoundaryEventTypeByNodeType[type] ?? type) : type
  const visuals = getNodeVisuals(effectiveType)
  const stateColor = preview.valid ? GraphColors.green_500 : GraphColors.red_500
  const stateBg = preview.valid ? GraphColors.green_50 : GraphColors.red_50

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    transform: `translate(${preview.position.x - visuals.width / 2}px, ${
      preview.position.y - visuals.height / 2
    }px)`,
    width: visuals.width,
    height: visuals.height,
    opacity: preview.snappedBoundary ? 1 : 0.7,
    pointerEvents: 'none',
    zIndex: 1000,
  }

  if (visuals.kind === BpmnNodeKind.ACTIVITY) {
    return (
      <ViewportPortal>
        <div
          data-testid="palette-drop-preview"
          data-valid={String(preview.valid)}
          style={{
            ...containerStyle,
            border: `1.5px solid ${stateColor}`,
            borderRadius: 8,
            background: GraphColors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* The preview shape and its icon are grouped around a shared centre
              and scales the icon 1.5× (getNodePreviewByType) — the icon belongs
              in the middle of the preview, not in its top-left corner */}
          <div
            style={{ color: GraphColors.neutral_400, transform: 'scale(1.5)', display: 'flex' }}
            dangerouslySetInnerHTML={{
              __html: recolorIconSvg(visuals.icon ?? '', GraphColors.neutral_400),
            }}
          />
        </div>
      </ViewportPortal>
    )
  }

  // Gateways/events are SVGs with hardcoded fills — a CSS `color` can't tint
  // them, so recolor the markup directly (as BpmnFlowNode does for its states).
  // The snapped-boundary preview mirrors the real boundary event, so it stays
  // untinted.
  const shapeSvg = preview.snappedBoundary
    ? (visuals.shape ?? '')
    : recolorShapeSvg(visuals.shape ?? '', stateColor, stateBg)
  const iconSvg =
    !visuals.icon || preview.snappedBoundary
      ? visuals.icon
      : recolorIconSvg(visuals.icon, stateColor)

  return (
    <ViewportPortal>
      <div
        data-testid="palette-drop-preview"
        data-valid={String(preview.valid)}
        style={containerStyle}
      >
        <div dangerouslySetInnerHTML={{ __html: shapeSvg }} />
        {iconSvg && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            dangerouslySetInnerHTML={{ __html: iconSvg }}
          />
        )}
      </div>
    </ViewportPortal>
  )
}
